import React, { useState } from 'react';
import axios from 'axios';
import { ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductCard({ product, shopSlug }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [imgSrc, setImgSrc] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);

  // For catalog: prefer clean image, fallback to original, then card
  const primaryImage = product.images?.find(i => i.type === 'clean')
    || product.images?.find(i => i.type === 'original')
    || product.images?.find(i => i.type === 'card')
    || product.images?.[0];

  React.useEffect(() => {
    if (!primaryImage) { setImgLoading(false); return; }
    const imgData = typeof primaryImage === 'string' ? { storage_path: primaryImage } : primaryImage;

    // Use external URL if available
    if (imgData.external_url) {
      setImgSrc(imgData.external_url);
      setImgLoading(false);
      return;
    }

    const path = imgData.storage_path;
    if (!path) { setImgLoading(false); return; }

    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(res => {
        setImgSrc(URL.createObjectURL(res.data));
        setImgLoading(false);
      })
      .catch(() => setImgLoading(false));
    return () => { if (imgSrc && imgSrc.startsWith('blob:')) URL.revokeObjectURL(imgSrc); };
  }, [primaryImage]);

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-2xl border border-[#E8E3D9] overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:scale-[0.98]"
      onClick={() => navigate(shopSlug ? `/prodotto/${product.id}?shop=${encodeURIComponent(shopSlug)}` : `/prodotto/${product.id}`)}
    >
      <div className="aspect-[4/5] relative overflow-hidden bg-[#F0EBE1]">
        {imgLoading ? (
          <div className="w-full h-full skeleton-shimmer" />
        ) : imgSrc ? (
          <img src={imgSrc} alt={product.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#877E78]">
            <ShoppingCart size={32} />
          </div>
        )}
        {product.original_price && product.original_price > product.price && (
          <span className="absolute top-2 left-2 bg-[#C25934] text-white text-xs font-bold px-2 py-1 rounded-full">
            -{Math.round((1 - product.price / product.original_price) * 100)}%
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-[#2A2624] text-sm line-clamp-2 leading-snug mb-1" style={{ fontFamily: 'Outfit' }}>
          {product.title}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[#C25934] font-bold text-lg" style={{ fontFamily: 'Outfit' }}>
            {product.price?.toFixed(2)} &euro;
          </span>
          {product.original_price && product.original_price > product.price && (
            <span className="text-[#877E78] text-xs line-through">
              {product.original_price.toFixed(2)} &euro;
            </span>
          )}
        </div>
        {product.category && product.category !== 'altro' && (
          <span className="inline-block mt-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-[#A64B2B] bg-[#F0EBE1] px-2 py-0.5 rounded-full">
            {product.category}
          </span>
        )}
      </div>
    </div>
  );
}
